import React from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ChevronRight as ChevronRightIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { TeamMember } from '../../models/types';
import { SurfaceCard } from '../ui/SurfaceCard';
import { InitialsAvatar } from '../ui/InitialsAvatar';
import { EmptyState } from '../ui/EmptyState';
import { formatPaceLabel, getMemberDisplayName } from './teamDetailFormatters';
import { MemberContestStats } from './useTeamMemberContestStats';
import { formatHandle } from '../ui/uiHelpers';
import { formatNumber } from '../../i18n/locale-format';
import { SuspiciousCountBadge } from '../contests/SuspiciousCountBadge';

interface TeamDetailMembersCardProps {
  members: TeamMember[];
  currentUserId: string | null;
  statsByUser: Record<string, MemberContestStats>;
  statsLoading: boolean;
  canManageMembers: boolean;
  onOpenMember: (userId: string) => void;
  onRemoveMember: (member: TeamMember) => void;
}

// Pace column drops out on narrow screens so rows fit 375px.
const columns = {
  xs: 'minmax(0, 1fr) 72px auto auto',
  sm: 'minmax(0, 1fr) 90px 90px auto auto',
};

// Members in leaderboard-style rows, biggest contributor first; clicking a row opens that athlete's activities.
export const TeamDetailMembersCard = ({ members, currentUserId, statsByUser, statsLoading, canManageMembers, onOpenMember, onRemoveMember }: TeamDetailMembersCardProps) => {
  const { t } = useTranslation('teams');
  const distanceOf = (userId: string) => statsByUser[userId]?.totalDistance ?? 0;
  const sorted = [...(members ?? [])].sort((a, b) => distanceOf(b.userId) - distanceOf(a.userId));

  return (
    <SurfaceCard title={t('membersCard.title')} subtitle={t('membersCard.subtitle', { count: members?.length ?? 0 })} bodyPadding={0}>
      {sorted.length > 0 ? (
        <Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: columns, gap: 1, p: '10px 16px', fontSize: 12, fontWeight: 500, color: 'var(--muted)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}>
            <span>{t('membersCard.member')}</span>
            <span style={{ textAlign: 'right' }}>{t('membersCard.distance')}</span>
            <Box component="span" sx={{ display: { xs: 'none', sm: 'block' }, textAlign: 'right' }}>{t('membersCard.avgPace')}</Box>
            <span style={{ width: canManageMembers ? 30 : 0 }} />
            <span style={{ width: 20 }} />
          </Box>
          {sorted.map(member => {
            const name = getMemberDisplayName(member);
            const isMe = member.userId === currentUserId;
            const stats = statsByUser[member.userId];
            return (
              <Box
                key={member.userId}
                onClick={() => onOpenMember(member.userId)}
                title={t('membersCard.viewActivities')}
                sx={{
                  cursor: 'pointer',
                  display: 'grid',
                  gridTemplateColumns: columns,
                  gap: 1,
                  alignItems: 'center',
                  p: '12px 16px',
                  fontSize: 14,
                  borderTop: '1px solid var(--border)',
                  fontVariantNumeric: 'tabular-nums',
                  backgroundColor: isMe ? 'var(--accent-bg)' : 'transparent',
                  '&:hover': { backgroundColor: isMe ? 'var(--accent-bg)' : 'var(--muted-bg)' },
                }}
              >
                <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center', minWidth: 0 }}>
                  <InitialsAvatar name={name} size={30} />
                  <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <Box component="span" sx={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {name}
                      <SuspiciousCountBadge count={stats?.suspiciousActivityCount} />
                    </Box>
                    {formatHandle(member.username) && (
                      <Box component="span" sx={{ fontSize: 12, color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {formatHandle(member.username)}
                      </Box>
                    )}
                  </Box>
                </Box>
                <Box component="span" sx={{ textAlign: 'right', fontWeight: 600 }}>
                  {statsLoading ? '...' : t('common:units.km', { value: formatNumber(stats?.totalDistance ?? 0, { minimumFractionDigits: 1, maximumFractionDigits: 1 }) })}
                </Box>
                <Box component="span" sx={{ display: { xs: 'none', sm: 'block' }, textAlign: 'right', color: 'var(--muted)' }}>
                  {statsLoading ? '...' : formatPaceLabel(stats?.averagePace ?? 0)}
                </Box>
                {canManageMembers ? (
                  <Tooltip title={t('membersCard.removeFromTeam')}>
                    <IconButton
                      aria-label={t('membersCard.removeFromTeam')}
                      onClick={e => {
                        e.stopPropagation();
                        onRemoveMember(member);
                      }}
                      size="small"
                      sx={{ color: 'var(--danger)' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <span />
                )}
                <ChevronRightIcon sx={{ color: 'var(--muted)', fontSize: 20 }} />
              </Box>
            );
          })}
        </Box>
      ) : (
        <EmptyState title={t('membersCard.emptyTitle')} description={t('membersCard.emptyDescription')} />
      )}
    </SurfaceCard>
  );
};
